import React, { ReactElement } from 'react'

import { ClassNames } from '@emotion/core'
import { Form, useFormikContext } from 'formik'

import { LoaderOverContent } from '@mindfulchefuk/components/Loader'
import {
  giftCardsAmount,
  sendGiftCardToRecipientMethods,
} from '@mindfulchefuk/config/giftCardsOptionsConfig'
import {
  Box,
  Button,
  buttonGroupStyles,
  Flex,
  Stack,
} from '@mindfulchefuk/design-system'
import { RadioAsButton } from '@mindfulchefuk/design-system/Forms/RadioButton/RadioAsButton'
import { convertSerializedStylesToString } from '@mindfulchefuk/design-system/system/utils/convertSerializedStylesToString'
import { Text } from '@mindfulchefuk/design-system/Text/Text'
import { RecipientFormState } from '@mindfulchefuk/features/GiftCards/interfaces'
import {
  FormControl,
  FormInput,
  FormLabel,
  FormRadioGroup,
  FormTextarea,
} from '@mindfulchefuk/forms'

export const RecipientForm = (): ReactElement => {
  const { handleSubmit, values, isSubmitting, isValid } =
    useFormikContext<RecipientFormState>()

  return (
    <Box position="relative">
      {isSubmitting && <LoaderOverContent />}
      <Form onSubmit={handleSubmit}>
        <Stack spacing={20}>
          <Text variant="heading-lg" as="h3">
            Give the gift of feel-good food
          </Text>
          <FormControl name="amount" id="amount">
            <FormLabel>Choose Amount</FormLabel>
            <FormRadioGroup>
              <Flex wrap="wrap" pb={24}>
                {giftCardsAmount.map(({ value, label }) => (
                  <Box key={value} mr={8} pb={16}>
                    <RadioAsButton
                      value={value}
                      data-testid={`giftcard-amount-${value}`}
                    >
                      £{label}
                    </RadioAsButton>
                  </Box>
                ))}
              </Flex>
            </FormRadioGroup>
          </FormControl>
        </Stack>
        <Stack spacing={40}>
          <Stack spacing={20}>
            <FormControl
              name="sendGiftCardToRecipient"
              id="sendGiftCardToRecipient"
            >
              <FormLabel>Send email to recipient</FormLabel>
              <ClassNames>
                {({ css, cx }) => (
                  <FormRadioGroup
                    className={convertSerializedStylesToString(
                      css,
                      cx,
                      buttonGroupStyles
                    )}
                  >
                    {sendGiftCardToRecipientMethods.map(({ value, label }) => (
                      <RadioAsButton key={value} value={value}>
                        {label}
                      </RadioAsButton>
                    ))}
                  </FormRadioGroup>
                )}
              </ClassNames>
              <Text mt={8} variant="body-sm" color="pepper">
                {values.sendGiftCardToRecipient === 'true'
                  ? "We'll send a voucher to them and a confirmation email to you."
                  : "We'll send both the voucher and confirmation email to you."}
              </Text>
            </FormControl>
          </Stack>
          <Stack spacing={16}>
            <Text variant="heading-md" as="h3">
              Who&apos;s the lucky person?
            </Text>
            <FormControl name="recipientFirstName" id="recipientFirstName">
              <FormLabel>Their first name</FormLabel>
              <FormInput />
            </FormControl>
            <FormControl name="recipientLastName" id="recipientLastName">
              <FormLabel>Their last name</FormLabel>
              <FormInput />
            </FormControl>
            {values.sendGiftCardToRecipient === 'true' && (
              <>
                <FormControl
                  name="recipientEmail"
                  id="recipientEmail"
                  type="email"
                >
                  <FormLabel>Their Email</FormLabel>
                  <FormInput />
                </FormControl>
                <FormControl
                  name="recipientEmailConfirm"
                  id="recipientEmailConfirm"
                  type="email"
                >
                  <FormLabel>Confirm their email address</FormLabel>
                  <FormInput />
                </FormControl>
              </>
            )}

            <FormControl name="personalMessage" id="personalMessage">
              <FormLabel>Add a personal message (Optional)</FormLabel>
              <FormTextarea maxLength={500} />
            </FormControl>
          </Stack>

          <Button
            variant="primary"
            size="large"
            fullWidth
            type="submit"
            disabled={!isValid}
          >
            Go to Payment
          </Button>
        </Stack>
      </Form>
    </Box>
  )
}
